import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll } from "motion/react";
import { ArrowDown, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Photo, Seo, useMediaQuery } from "../../kit.jsx";
import { AAROH_IMAGES } from "../../data/images.js";
import { guides } from "../../data/content.js";
import { Rise } from "./shared.jsx";
import "./join.css";

const STEPS = [
  {
    id: "step-1",
    title: "Find a programme",
    text: "Read the programme accounts to understand the work, then check upcoming events for an opening. Past event pages are recaps, not open calls. Programmes are also where you meet other young builders.",
    links: [["Programmes", "/programmes"], ["Upcoming events", "/programmes/upcoming"], ["Free opportunities", "/student-opportunities"]],
    image: AAROH_IMAGES.roomWide,
    alt: "The main hall at AAROH, IIIT Delhi, with students at their desks",
    caption: "AAROH at IIIT Delhi. The main hall on day one.",
  },
  {
    id: "step-2",
    title: "Pick a problem",
    text: "No idea yet? That is normal. Start by noticing a repeated difficulty at school or in your community, and who it affects.",
    links: [["Find a real-world problem", "/guides/find-a-real-world-problem"], ["Problems to investigate", "/problems"]],
    image: AAROH_IMAGES.studentDiscussion,
    alt: "Students talking through an idea during a discussion at AAROH",
    caption: "AAROH. Students talking an idea through.",
  },
  {
    id: "step-3",
    title: "Build a first version",
    text: "Describe who has the problem and what happens now. Then choose a small version you can test, and build it with other students.",
    links: [["Write a problem statement", "/guides/write-a-problem-statement"], ["From idea to prototype", "/guides/idea-to-prototype"]],
    image: AAROH_IMAGES.buildSession,
    alt: "Students and a mentor working together around laptops at AAROH",
    caption: "AAROH. Teams and mentors at work.",
  },
  {
    id: "step-4",
    title: "Show your work",
    text: "Explain what you made, what you learned and what you would fix next. A clear, honest pitch beats a polished one.",
    links: [["Pitch a student project", "/guides/pitch-a-student-project"], ["Your first student hackathon", "/guides/beginner-hackathon-guide"]],
    image: AAROH_IMAGES.studentPresentation,
    alt: "A student presenting at a lectern at AAROH",
    caption: "AAROH. A student presents to the room.",
  },
];

const WHERE = [
  {
    label: "I want to join a programme",
    step: 0,
    text: "Read the programme accounts to understand the work, then check upcoming events for an opening.",
    links: [["Programmes", "/programmes"], ["Upcoming events", "/programmes/upcoming"]],
  },
  {
    label: "I have an idea",
    step: 2,
    text: "Describe who has the problem and what happens now. Then choose a small version you can test.",
    links: [["Write a problem statement", "/guides/write-a-problem-statement"], ["Make a first prototype", "/guides/idea-to-prototype"]],
  },
  {
    label: "I don't have an idea yet",
    step: 1,
    text: "Start by noticing a repeated difficulty at school or in your community.",
    links: [["Find a problem", "/guides/find-a-real-world-problem"], ["Browse investigation topics", "/problems"]],
  },
  {
    label: "I want to help run GRID",
    step: null,
    text: "Explore contributing to the team. A specific role and time commitment need to be agreed before you begin.",
    links: [["Team enquiries", "/student-opportunities/team"]],
  },
];

function WherePicker() {
  const reduce = useReducedMotion();
  const [picked, setPicked] = useState(0);
  const item = WHERE[picked];
  return (
    <Rise className="jn-where" delay={0.55}>
      <p className="jn-mono" id="jn-where-label">Where are you right now?</p>
      <div className="jn-where__pills" role="radiogroup" aria-labelledby="jn-where-label">
        {WHERE.map((option, index) => (
          <button
            key={option.label}
            type="button"
            role="radio"
            aria-checked={picked === index}
            className={`jn-pill ${picked === index ? "is-on" : ""}`}
            onClick={() => setPicked(index)}
          >
            {picked === index && (
              <motion.span className="jn-pill__fill" layoutId="jn-where-fill" transition={{ duration: reduce ? 0 : 0.45, ease: EASE_OUT }} />
            )}
            <span className="jn-pill__label">{option.label}</span>
          </button>
        ))}
      </div>
      <div className="jn-where__panel" aria-live="polite">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={item.label}
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.32, ease: EASE_OUT }}
          >
            <p>{item.text}</p>
            <div className="jn-where__links">
              {item.links.map(([label, to]) => (
                <Link key={to} className="gh-link" to={to}>{label} <ArrowRight weight="bold" aria-hidden="true" /></Link>
              ))}
              {item.step !== null && (
                <a className="jn-where__jump" href={`#${STEPS[item.step].id}`}>
                  Start at step {item.step + 1} <ArrowDown weight="bold" aria-hidden="true" />
                </a>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </Rise>
  );
}

function StepLinks({ links }) {
  return (
    <ul className="jn-steps__links">
      {links.map(([label, to]) => (
        <li key={to}>
          <Link to={to}>
            <span>{label}</span>
            <ArrowUpRight weight="bold" aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function StepScene() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const wide = useMediaQuery("(min-width: 1000px)");
  const scene = wide;
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end center"] });
  useMotionValueEvent(scrollYProgress, "change", (value) => {
    const next = Math.min(STEPS.length - 1, Math.max(0, Math.floor(value * STEPS.length)));
    setActive((previous) => (previous === next ? previous : next));
  });

  if (!scene) {
    return (
      <ol className="jn-steps jn-steps--stack">
        {STEPS.map((step, index) => (
          <li key={step.id} id={step.id} className="jn-steps__item">
            <span className="jn-steps__num">{String(index + 1).padStart(2, "0")}</span>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
            <ClipPhoto src={step.image} alt={step.alt} caption={step.caption} sizes="100vw" />
            <StepLinks links={step.links} />
          </li>
        ))}
      </ol>
    );
  }

  return (
    <div className="jn-steps jn-steps--scene" ref={ref}>
      <div className="jn-steps__media">
        <div className="jn-steps__frame">
          {STEPS.map((step, index) => (
            <motion.div
              key={step.id}
              className="jn-steps__shot"
              initial={false}
              animate={{ clipPath: index <= active ? "inset(0% 0% 0% 0%)" : "inset(100% 0% 0% 0%)" }}
              transition={{ duration: reduce ? 0 : 0.9, ease: [0.65, 0, 0.35, 1] }}
              style={{ zIndex: index }}
            >
              <motion.div
                className="jn-steps__zoom"
                initial={false}
                animate={{ scale: index === active || reduce ? 1 : 1.08 }}
                transition={{ duration: reduce ? 0 : 1.2, ease: EASE_OUT }}
              >
                <Photo src={step.image} alt={index === active ? step.alt : ""} sizes="50vw" />
              </motion.div>
            </motion.div>
          ))}
          <div className="jn-steps__counter" aria-hidden="true">
            <span className="jn-steps__count-mask">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={active}
                initial={reduce ? false : { y: "100%" }}
                animate={{ y: "0%" }}
                exit={reduce ? undefined : { y: "-100%" }}
                transition={{ duration: 0.6, ease: EASE_OUT }}
              >
                {String(active + 1).padStart(2, "0")}
              </motion.span>
            </AnimatePresence>
            </span>
            <small>/04</small>
          </div>
        </div>
        <p className="jn-steps__caption">{STEPS[active].caption}</p>
        <div className="jn-steps__rail" aria-hidden="true">
          {STEPS.map((step, index) => (
            <span key={step.id} className={index <= active ? "is-on" : ""} />
          ))}
        </div>
      </div>
      <ol className="jn-steps__list">
        {STEPS.map((step, index) => (
          <li key={step.id} id={step.id} className={`jn-steps__item ${index === active ? "is-active" : ""}`}>
            <span className="jn-steps__num">{String(index + 1).padStart(2, "0")}</span>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
            <StepLinks links={step.links} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function GuideShelf() {
  return (
    <section className="jn-wrap jn-shelf" aria-labelledby="jn-shelf-title">
      <div className="jn-shelf__head">
        <MaskWords id="jn-shelf-title" className="jn-h2" text="Five free guides for the work in between." />
        <Link className="gh-link" to="/guides">All guides <ArrowRight weight="bold" aria-hidden="true" /></Link>
      </div>
      <ul className="jn-shelf__list">
        {guides.map((guide) => (
          <li key={guide.slug}>
            <Link to={`/guides/${guide.slug}`}>
              <strong>{guide.title}</strong>
              <span>{guide.summary}</span>
              <small className="jn-mono">{guide.readTime} read</small>
              <ArrowUpRight weight="bold" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function ForStudentsPage() {
  return (
    <PageFrame className="rd-page jn-page jn-st">
      <Seo
        title="For students"
        path="/for-students"
        description="Bring curiosity, a problem you care about or simply the willingness to learn by building."
      />
      <main>
        <header className="jn-wrap jn-st-hero">
          <div className="jn-st-hero__main">
            <Rise as="p" className="jn-mono" delay={0.05}>For students</Rise>
            <MaskWords as="h1" className="jn-h1 jn-st-hero__title" text="You do not need permission to start." onMount delay={0.1} />
            <Rise as="p" className="jn-lede" delay={0.45}>
              Bring curiosity, a problem you care about or simply the willingness to learn by building. Four steps take you from finding a programme to showing your work. You do not need a finished idea to begin.
            </Rise>
          </div>
          <WherePicker />
        </header>
        <section className="jn-wrap jn-st-steps" aria-labelledby="jn-st-steps-title">
          <MaskWords id="jn-st-steps-title" className="jn-h2" text="Four steps. Start at any of them." />
          <StepScene />
        </section>
        <GuideShelf />
        <section className="jn-wrap jn-st-help" aria-label="Help run GRID">
          <p>
            <strong>Rather help run GRID?</strong> The team is made of school students. Tell us the work you want to do and the time you can give.
          </p>
          <Link className="gh-btn gh-btn--ghost" to="/student-opportunities/team">
            <span className="roll" data-label="Team enquiries"><span>Team enquiries</span></span>
          </Link>
        </section>
      </main>
    </PageFrame>
  );
}
